/**
 * The first two sentences a county is described with, on the dashboard and
 * on the page an ad lands on.
 *
 * Both used to be written inline, and both overclaimed in the same way: the
 * fallback headline for a county with no buildings read "N parcels carry an
 * open case in the public record", and nine live counties have no case feed
 * at all — Manatee's 14,000 scored parcels carry an absentee, foreign or
 * long-held owner off the property roll, and not one open case. The lede
 * under it named "open code cases, liens and dropped homesteads" for every
 * county, published or not. Under a paid ad that is a promise the county
 * cannot keep, and the page has to be right *before* the caveats lower down.
 *
 * So these are derived from the county's own signal list, the rule
 * `countyDepth` and `scoreScope` already follow: a sentence computed from the
 * served list cannot outlive its own truth. `signals` undefined means "every
 * county" or "not loaded yet", and both name the full set — some county holds
 * each of these things, and a placeholder naming none would be wrong rather
 * than vague.
 */

import type { HeadlineStats } from "../api/types";
import { fmt } from "./format";

/** A headline in three pieces, so the page can emphasise the middle one. */
export interface Headline {
  before: string;
  emphasis: string;
  after: string;
}

/**
 * The signals that only a code-enforcement case register can evidence. The
 * same set `countyDepth` keeps, spelled out here rather than imported because
 * that module's is private and the two are pinned to each other by a test.
 */
export const CASE_SIGNALS = [
  "code_enforcement",
  "unsafe_structure",
  "lien_recorded",
  "other_violation",
];

function publishes(signals: string[] | undefined, flag: string): boolean {
  return signals ? signals.includes(flag) : true;
}

function hasCases(signals?: string[]): boolean {
  return CASE_SIGNALS.some((flag) => publishes(signals, flag));
}

/**
 * The headline. Buildings first, because the milestone reading is what the
 * product is about; a county that rolls up no buildings gets a sentence about
 * its parcels that says what they carry — a case only where a case feed is
 * read, otherwise a signal off the roll.
 */
export function headline(
  where: string,
  stats: HeadlineStats | null | undefined,
  signals?: string[],
): Headline {
  const inWhere = where ? ` in ${where}` : "";
  if (stats && stats.buildings > 0) {
    return {
      before: `${fmt(stats.overdue)} condo buildings${inWhere} look `,
      emphasis: "past due",
      after: " for milestone inspection",
    };
  }
  if (stats) {
    return hasCases(signals)
      ? {
          before: `${fmt(stats.units)} parcels${inWhere} carry an `,
          emphasis: "open case",
          after: " in the public record",
        }
      : {
          before: `${fmt(stats.units)} parcels${inWhere} carry a `,
          emphasis: "distress signal",
          after: " on the property roll",
        };
  }
  return {
    before: `Structural distress${inWhere}, `,
    emphasis: "from the county record",
    after: "",
  };
}

/**
 * What the score is built from, naming only what this county publishes.
 *
 * The owner signals are summarised as one phrase rather than listed — every
 * live county declares `absentee`, and a lede is not the guide's table. The
 * case-derived nouns are the ones that go missing, and the ones an ad is
 * tempted to promise.
 */
export function lede(
  possessive: string,
  stats: HeadlineStats | null | undefined,
  signals?: string[],
): string {
  const carried: string[] = [];
  if (
    publishes(signals, "code_enforcement") ||
    publishes(signals, "unsafe_structure") ||
    publishes(signals, "other_violation")
  ) {
    carried.push("open code cases");
  }
  if (publishes(signals, "lien_recorded")) carried.push("liens");
  if (publishes(signals, "homestead_lost")) carried.push("dropped homesteads");
  carried.push("out-of-state owners");

  const buildings = !stats || stats.buildings > 0;
  const subject = buildings
    ? `Buildings overdue for their milestone inspection, parcels carrying ${list(carried)}`
    : `Parcels carrying ${list(carried)}`;
  const source = hasCases(signals)
    ? `${possessive} own registers`
    : `${possessive} property roll`;
  const unit = buildings ? "building by building" : "parcel by parcel";
  return (
    `${subject} — read from ${source} and scored ${unit}, so the ones ` +
    `worth a call are at the top of the list rather than somewhere in it.`
  );
}

function list(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
